import React, { useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import { State } from '../types/state'
import { Button, Fab, Paper, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Contract } from '../types/contracts'
import DeleteContractDialog from './DeleteContractDialog'
import NewOrEditContractDialog from './NewOrEditContractDialog'
import { useRequests } from '../requests/useRequests'
import { getFileFromBuffer } from '../browser/utils'
import { RefreshActions } from '../state/refresh'
import { useIsMobileView } from '../hooks/useIsMobileView'
import { modernCard } from '../styles/modern'
import { typographyStyles } from '../styles/typography'

const Contracts = (): JSX.Element => {
  const dispatch = useDispatch()
  const { contracts, contractors } = useSelector((state: State) => state.user)
  const isMobileView = useIsMobileView()
  const requestMaker = useRequests()

  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([])
  const [selectedRowIdsToDelete, setSelectedRowIdsToDelete] = useState<
  number[]
  >([])
  const [contract, setContract] = useState<Contract | null>(null)
  const [isNewOrEditContractDialogOpen, setIsNewOrEditContractDialogOpen] =
    useState(false)
  const [isDeleteContractDialogOpen, setIsDeleteContractDialogOpen] =
    useState(false)

  const ContractorColumn: React.FC<{ id: number }> = ({ id }) => {
    const contract = contracts.find(c => c.id === id) as Contract
    const contractor = contractors.find(c => c.id === contract.contractorId)

    return <>{contractor?.name}</>
  }

  const OptionsButton: React.FC<{ id: number }> = ({ id }) => {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Fab
          size='small'
          onClick={() => {
            setContract(contracts.find(c => c.id === id) as Contract)
            setIsNewOrEditContractDialogOpen(true)
          }}
          sx={{
            background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
            color: 'white',
            transform: 'scale(0.85)',
            boxShadow: '0 4px 10px rgba(237, 137, 54, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ed8936 0%, #f6ad55 100%)',
              transform: 'scale(0.9)'
            }
          }}
          aria-label='edit'
        >
          <EditIcon />
        </Fab>
        <Fab
          size='small'
          onClick={() => {
            setSelectedRowIdsToDelete([id])
            setIsDeleteContractDialogOpen(true)
          }}
          sx={{
            background: 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)',
            color: 'white',
            transform: 'scale(0.85)',
            boxShadow: '0 4px 10px rgba(245, 101, 101, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #f56565 0%, #fc8181 100%)',
              transform: 'scale(0.9)'
            }
          }}
          aria-label='delete'
        >
          <DeleteIcon />
        </Fab>
        <Button
          variant='contained'
          style={{
            fontSize: 12,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)',
            padding: '6px 16px'
          }}
          onClick={() => {
            void (async () => {
              try {
                const receipts = await requestMaker.generateContractReceipts([id])
                dispatch(RefreshActions.refreshUserState())
                getFileFromBuffer(
                  receipts.data,
                  'application/pdf',
                  isMobileView ? 'receipts.pdf' : undefined
                )
              } catch (error) {
                dispatch(RefreshActions.setToastMessage({
                  message: 'Failed to generate receipt'
                }))
              }
            })()
          }}
        >
          Generate receipt
        </Button>
      </Box>
    )
  }

  const columns: GridColDef[] = [
    { field: 'service', headerName: 'Service', flex: 1.2, minWidth: 130 },
    { field: 'unitNetto', headerName: 'Unit netto', width: 90 },
    { field: 'vatRate', headerName: 'Vat rate', width: 80 },
    { field: 'quantity', headerName: 'Quantity', width: 80 },
    {
      field: 'contractor',
      headerName: 'Contractor',
      flex: 1,
      minWidth: 100,
      renderCell: params => <ContractorColumn id={params.row.id} />
    },
    {
      field: 'options',
      headerName: '',
      flex: 1.8,
      minWidth: 260,
      renderCell: params => <OptionsButton id={params.row.id} />
    }
  ]

  return (
    <Box
      sx={{
        width: isMobileView ? '85vw' : '80vw',
        margin: 'auto',
        marginBottom: '40px',
        padding: isMobileView ? '20px' : '30px'
      }}
    >
      <Paper elevation={0} style={modernCard} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Contracts Management
        </h2>
        <Box sx={{ display: 'flex', gap: 2, marginBottom: '20px', flexWrap: 'wrap' }}>
          <Fab
            size='medium'
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                transform: 'scale(1.05)'
              }
            }}
            aria-label='add'
            onClick={() => {
              setContract(null)
              setIsNewOrEditContractDialogOpen(true)
            }}
            disabled
          >
            <AddIcon />
          </Fab>
          <Fab
            size='medium'
            onClick={() => {
              setSelectedRowIdsToDelete(selectedRowIds)
              setIsDeleteContractDialogOpen(true)
            }}
            sx={{
              background: 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(245, 101, 101, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f56565 0%, #fc8181 100%)',
                transform: 'scale(1.05)'
              }
            }}
            aria-label='delete'
            disabled={selectedRowIds.length === 0 || true}
          >
            <DeleteIcon />
          </Fab>
          <Button
            variant='contained'
            style={{
              marginLeft: isMobileView ? '0' : 'auto',
              fontSize: isMobileView ? '12px' : '14px',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
            disabled={selectedRowIds.length === 0}
            onClick={() => {
              void (async () => {
                try {
                  const receipts = await requestMaker.generateContractReceipts(
                    selectedRowIds
                  )
                  dispatch(RefreshActions.refreshUserState())
                  getFileFromBuffer(
                    receipts.data,
                    'application/pdf',
                    isMobileView ? 'receipts.pdf' : undefined
                  )
                } catch (error) {
                  dispatch(RefreshActions.setToastMessage({
                    message: 'Failed to generate receipts'
                  }))
                }
              })()
            }}
          >
            Generate receipts
          </Button>
        </Box>
        <Box sx={{ height: isMobileView ? '60vh' : '55vh', width: '100%', overflow: 'hidden' }}>
          <DataGrid
            disableRowSelectionOnClick
            rows={contracts}
            columns={columns}
            checkboxSelection
            onRowSelectionModelChange={newSelection => {
              setSelectedRowIds(newSelection as number[])
            }}
            sx={{
              border: 'none',
              width: '100%',
              '& .MuiDataGrid-cell:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px 8px 0 0',
                fontWeight: 600
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.05)'
              }
            }}
          />
        </Box>
      </Paper>
      {isNewOrEditContractDialogOpen && (
        <NewOrEditContractDialog
          handleClose={() => setIsNewOrEditContractDialogOpen(false)}
          contract={contract}
          contractors={contractors}
        />
      )}
      {isDeleteContractDialogOpen && (
        <DeleteContractDialog
          handleClose={() => setIsDeleteContractDialogOpen(false)}
          ids={selectedRowIdsToDelete}
        />
      )}
    </Box>
  )
}

export default Contracts
